/**
 * Investec / dealer payment notification record shape from
 * `GET /dealer/investec-notifications/`.
 *
 * @typedef {Object} PaymentNotification
 * @property {number} id
 * @property {string} last_updated
 * @property {string} created_at
 * @property {boolean} is_deleted
 * @property {string} message_type
 * @property {string} nostro_agent
 * @property {string} sender_reference
 * @property {string} value_date
 * @property {string} currency
 * @property {string} amount
 * @property {string} sender_bank_bic
 * @property {string} sender_bank_name
 * @property {string} sender_to_receiver_info
 * @property {string} ordering_customer_account_id
 * @property {string} ordering_customer_name
 * @property {string} ordering_customer_address_line_1
 * @property {string} ordering_customer_address_line_2
 * @property {string} ordering_customer_address_line_3
 * @property {string} ordering_institution_bic
 * @property {string} ordering_institution_name
 * @property {string} beneficiary_customer_account_id
 * @property {string} beneficiary_customer_name
 * @property {string} payment_details_remittance_information
 * @property {string} payment_details_details_of_charges
 * @property {boolean} is_actioned
 * @property {number} [notes_total] Read-only count of notes attached to this notification.
 */

/** Columns shown on the payment notifications table (order preserved). */
export const PAYMENT_NOTIFICATION_TABLE_COLUMNS = [
  { key: "created_at", label: "Created at" },
  { key: "nostro_agent", label: "Nostro agent" },
  { key: "currency", label: "Currency" },
  { key: "amount", label: "Amount" },
  { key: "beneficiary_customer_name", label: "Beneficiary customer name" },
  { key: "ordering_customer_name", label: "Ordering customer name" },
  { key: "is_actioned", label: "Actioned" },
  { key: "notes", label: "Notes" },
];

/** Detail modal: fields grouped by section (title + fields). */
export const PAYMENT_NOTIFICATION_DETAIL_SECTIONS = [
  {
    title: "Notification overview",
    fields: [
      { key: "created_at", label: "Created at" },
      { key: "last_updated", label: "Last updated" },
      { key: "message_type", label: "Message type" },
      { key: "nostro_agent", label: "Nostro agent" },
      { key: "sender_reference", label: "Sender reference" },
      { key: "value_date", label: "Value date" },
      { key: "currency", label: "Currency" },
      { key: "amount", label: "Amount" },
    ],
  },
  {
    title: "Sender bank",
    fields: [
      { key: "sender_bank_bic", label: "Sender bank BIC" },
      { key: "sender_bank_name", label: "Sender bank name" },
      { key: "sender_to_receiver_info", label: "Sender to receiver info" },
    ],
  },
  {
    title: "Ordering customer",
    fields: [
      { key: "ordering_customer_account_id", label: "Account ID" },
      { key: "ordering_customer_name", label: "Name" },
      { key: "ordering_customer_address_line_1", label: "Address line 1" },
      { key: "ordering_customer_address_line_2", label: "Address line 2" },
      { key: "ordering_customer_address_line_3", label: "Address line 3" },
    ],
  },
  {
    title: "Ordering institution",
    fields: [
      { key: "ordering_institution_bic", label: "BIC" },
      { key: "ordering_institution_name", label: "Name" },
    ],
  },
  {
    title: "Beneficiary customer",
    fields: [
      { key: "beneficiary_customer_account_id", label: "Account ID" },
      { key: "beneficiary_customer_name", label: "Name" },
    ],
  },
  {
    title: "Payment details",
    fields: [
      {
        key: "payment_details_remittance_information",
        label: "Remittance information",
      },
      {
        key: "payment_details_details_of_charges",
        label: "Details of charges",
      },
    ],
  },
  {
    title: "Status",
    fields: [{ key: "is_actioned", label: "Actioned" }],
  },
];
